import React from 'react';
import { useForm } from 'react-hook-form';
import { V2Button } from '../../shared/custom-ui/v2-components';
import { Input } from '../../shared/custom-ui';

interface FormProps {
    formState: {
        email: string;
        first_name?: string;
        middle_name?: string;
        last_name?: string;
    };
    onSubmit: (data: { email: string; first_name?: string; middle_name?: string; last_name?: string }) => void;
    loading?: boolean;
    onClose: () => void;
    initialData?: object;
}

const InputBase = ({
    id, label, error, children,
}: { id: string; label: string; error?: string; children: React.ReactNode }) => (
    <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
            {label}
        </label>
        {children}
        {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
);

const fieldClass = (hasError?: boolean) =>
    [
        'w-full rounded-lg border px-3 py-2.5 text-sm shadow-sm outline-none',
        'focus:border-primary-400 focus:ring-2 focus:ring-primary-200',
        hasError ? 'border-red-500' : '',
    ].join(' ');

const FormComponent: React.FC<FormProps> = ({ formState, onSubmit, loading, onClose, initialData }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: formState,
    });

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Name fields: First (required) · Middle · Last */}
            <div className="grid gap-4 sm:grid-cols-3">
                <InputBase id="first_name" label="First Name *" error={errors.first_name?.message as string | undefined}>
                    <Input
                        id="first_name"
                        type="text"
                        {...register('first_name', { required: 'First name is required' })}
                        placeholder="First Name"
                        className={fieldClass(Boolean(errors.first_name))}
                        disabled={loading}
                    />
                </InputBase>

                <InputBase id="middle_name" label="Middle Name">
                    <Input
                        id="middle_name"
                        type="text"
                        {...register('middle_name')}
                        placeholder="Optional"
                        className={fieldClass()}
                        disabled={loading}
                    />
                </InputBase>

                <InputBase id="last_name" label="Last Name">
                    <Input
                        id="last_name"
                        type="text"
                        {...register('last_name')}
                        placeholder="Optional"
                        className={fieldClass()}
                        disabled={loading}
                    />
                </InputBase>
            </div>

            {/* Email — full width */}
            <InputBase id="email" label="Email *" error={errors.email?.message as string | undefined}>
                <Input
                    id="email"
                    type="email"
                    {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+$/i, message: 'Invalid email format' },
                    })}
                    placeholder="name@company.com"
                    className={fieldClass(Boolean(errors.email))}
                    disabled={loading}
                />
            </InputBase>

            <div className="pt-3 flex justify-end gap-3">
                <V2Button variant="danger" onClick={onClose} disabled={loading}>
                    Cancel
                </V2Button>
                <V2Button variant="primary" type="submit" disabled={loading} loading={loading}>
                    {loading ? 'Saving...' : (initialData ? 'Update Member' : 'Add Member')}
                </V2Button>
            </div>
        </form>
    );
};

export default FormComponent;
